import type { Registry as Services } from '@ember/service';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { tKey } from 'ember-intl';
import {
  COLOR_BACKGROUND_NEUTRAL_DEFAULT,
  getCssVariableString,
} from 'frontend-utils/utils/css-variable';
import * as Highcharts from 'highcharts';
import { LATEST } from 'lumen/utils/constants/pi-constant-values';
import { getPeriodsInRange } from 'lumen/utils/dates/moment-helpers';
import { getFormattedLastPossibleExtractDate } from 'lumen/utils/parity-analytics/date-utils';

const DAY_IN_SECONDS = 24 * 60 * 60;
const WEEK_IN_SECONDS = 7 * 24 * 60 * 60;

/**
 * Gets the xValue for a given timestamp
 * This is used throughout the app in charts as a readable way to convert timestamps into x-values
 * where the chart's x-axis is time-related
 * @param  {Number} timestamp Unix timestamp
 * @return {Number}           Time value for x-axis in our charts
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-redundant-type-constituents
export function xValue(timestamp: string | number | typeof LATEST) {
  if (timestamp === LATEST) {
    // Use a fixed width of 4 days for the LATEST period to indicate it's not a full week of data
    return dayjs
      .utc(getFormattedLastPossibleExtractDate())
      .add(4, 'day')
      .unix();
  }

  return dayjs.utc(timestamp).unix();
}

/**
 * Gets tomorrows xValue for a given timestamp.
 * Does what xValue() does, but adds a full day to it.
 * @param  {Number} timestamp Unix timestamp
 * @return {Number}           Time value for x-axis in our charts
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function xValueTomorrow(timestamp: number | string) {
  return xValue(timestamp) + DAY_IN_SECONDS;
}

/**
 * Gets the difference in consecutive xValues
 * @param  {Boolean} isDailyAggregation True if the graph is in day view
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function xValueDifference(isDailyAggregation = true) {
  return isDailyAggregation ? DAY_IN_SECONDS : WEEK_IN_SECONDS;
}

/**
 * checks if a date is a weekend date
 * @param { dayjs } dayMoment : day to check
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function isWeekendDate(dayMoment: Dayjs) {
  return dayMoment.day() === 5 || dayMoment.day() === 6;
}

/**
 * creates the plotbands for the weekends
 * @param { object } hotelAndMarketService : service needed for time conversion
 * @param { dayjs } startDate : day to start checking
 * @param { dayjs } endDate : day to stop checking
 * @param { string } color : color of the bands
 */
export function getWeekendPlotBands(
  hotelAndMarketService: Services['hotel-and-market'],
  startDate: Dayjs,
  endDate: Dayjs,
  color = getCssVariableString(COLOR_BACKGROUND_NEUTRAL_DEFAULT),
): Highcharts.XAxisPlotBandsOptions[] {
  const days = getPeriodsInRange(startDate, endDate, 'day');
  const weekendPlotBands: { color: string; from: number; to: number }[] = [];
  days.forEach((dayMoment) => {
    if (isWeekendDate(dayMoment)) {
      // mark weekends (friday and saturday)
      weekendPlotBands.push({
        from: hotelAndMarketService.toLocalUnixTime(
          dayMoment.subtract(12, 'hour').unix(),
        ),
        to: hotelAndMarketService.toLocalUnixTime(
          dayMoment.add(12, 'hour').unix(),
        ),
        color,
      });
    }
  });
  return weekendPlotBands;
}

/**
 * Collect mean data for a given point in the rates chart
 * and add it to the passed-in collection
 * @param  {Array}  collection     The collection of points which will be used to calculate mean data
 * @param  {Object}  dataPoint      A point generated by rates-chart utils containing x, y, tooltipValue, etc...
 * @param  {Boolean} isCurrentHotel True if the current point is the user's hotel
 * @param  {Number}  indexInChart   The index of the current dataPoint in the chart
 */

export function collectMeanData(
  collection: Record<
    number,
    (Partial<Highcharts.Point> & {
      isCurrentHotel?: boolean;
      tooltipValue?: string;
    })[]
  >,
  dataPoint: Partial<Highcharts.Point> & {
    isCurrentHotel?: boolean;
    tooltipValue?: string;
  },
  isCurrentHotel: boolean,
  indexInChart: number,
): void {
  const item = collection[indexInChart];
  if (item) {
    item.push({
      x: dataPoint.x,
      y: dataPoint.y,
      isCurrentHotel,
      tooltipValue: dataPoint.tooltipValue,
    });
  } else {
    collection[indexInChart] = [
      {
        x: dataPoint.x,
        y: dataPoint.y,
        isCurrentHotel,
        tooltipValue: dataPoint.tooltipValue,
      },
    ];
  }
}

/**
 * Stacks a list of data points for a solidgauge type chart.
 * The util function expects the y values in the 'originalY' property on the data point
 * and puts it in the calculated, stacked y value in y.
 * @param  {Array}  data  The list of data points to stack.
 * @return {Array}        The list of data points with newly calculated y values.
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function stackGaugeChartData(
  data: (Highcharts.Point & { originalY: number })[],
) {
  const stackedData: Highcharts.Point[] = [];
  let cumulativeY = 0;
  data.forEach((dataPoint) => {
    cumulativeY += dataPoint.originalY;
    dataPoint.y = cumulativeY;
    stackedData.unshift(dataPoint); // Add the item back in reverse order
  });

  return stackedData;
}

/**
 * Tick positioner for a chart that supports day and week aggregation.
 * This will calculate the amount of ticks to use based on the date range and date aggregation,
 * so that the labels are readable and don't get too close to eachother.
 * @param  {number}  dataMin  The dataMin (least) value provided by the Highcharts tickPositioner context.
 * @param  {number}  dataMax  The dataMax (most) value provided by the Highcharts tickPositioner context.
 * @param  {boolean} isDailyDataAggregation  Whether the data is aggregated by day or not.
 * @return {Array}        The list of tick positions for which to show labels.
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function tickPositionerDayWeek(
  dataMin: number,
  dataMax: number,
  isDailyDataAggregation: boolean,
) {
  const positions = [];
  // Highcharts would put a label every week which became unreadable
  // This is a simple way of spreading the labels
  if (dataMax !== null && dataMin !== null) {
    // Note: dataMin and startData.unix() * 1000 should be the same
    // If you adjust according to timezone this will no longer be the case
    // This is why we just use dayjs here
    const startMoment = dayjs.unix(dataMin / 1000);
    const endMoment = dayjs.unix(dataMax / 1000);
    const months = endMoment.diff(startMoment, 'month');
    const weeks = endMoment.diff(startMoment, 'week');
    const days = endMoment.diff(startMoment, 'day');

    let increment = null;

    // Put the labels further apart, depending on the number of weeks
    // All values are empirically determined, so it's what seemed good enough
    // Set thresholds to show labels every tick when the range is smaller

    // Config for aggregation by day
    if (isDailyDataAggregation) {
      if (days <= 10) {
        increment = startMoment.add(1, 'day').unix() * 1000 - dataMin;
      } else if (months < 1) {
        increment = startMoment.add(3, 'day').unix() * 1000 - dataMin;
      } else if (months < 2) {
        increment = startMoment.add(5, 'day').unix() * 1000 - dataMin;
      } else if (months < 3) {
        increment = startMoment.add(1, 'week').unix() * 1000 - dataMin;
      } else if (months < 6) {
        increment = startMoment.add(2, 'week').unix() * 1000 - dataMin;
      } else {
        increment = startMoment.add(4, 'week').unix() * 1000 - dataMin;
      }
    }

    // Config for aggregation by week
    if (!isDailyDataAggregation) {
      // No else ifs for better readability
      if (weeks > 50) {
        increment = startMoment.add(8, 'week').unix() * 1000 - dataMin;
      } else if (weeks > 25) {
        increment = startMoment.add(6, 'week').unix() * 1000 - dataMin;
      } else if (weeks > 10) {
        increment = startMoment.add(4, 'week').unix() * 1000 - dataMin;
      } else if (weeks > 6) {
        increment = startMoment.add(3, 'week').unix() * 1000 - dataMin;
      } else if (weeks > 3) {
        increment = startMoment.add(2, 'week').unix() * 1000 - dataMin;
      } else {
        increment = startMoment.add(1, 'week').unix() * 1000 - dataMin;
      }
    }

    for (
      let tick = Math.floor(dataMin);
      increment !== null && tick - increment < dataMax;
      tick += increment
    ) {
      positions.push(tick);
    }
  }

  return positions;
}

// This tick positioner always adds a tick for the first and last day/week.
// It was created because we show a warning symbol in the label for partially included weeks in the date range.
// We want the warning label to always be shown if there is one, and the easiest way to do that is to always show a label for the first and last week.
export function tickPositionerPartialWeeks(
  series: Highcharts.Series[],
  desiredLabelCount: number,
): Highcharts.AxisTickPositionsArray {
  const distinctXValues = new Set<number>();

  // Collect distinct x values from all series
  for (let i = 0; i < series.length; i++) {
    // `xData` is not public, see https://github.com/highcharts/highcharts/issues/17698.
    const xValues =
      (series[i] as Highcharts.Series & { xData: number[] })?.xData ?? [];
    for (let j = 0; j < xValues.length; j++) {
      const xValuesEntry = xValues[j];
      if (xValuesEntry !== undefined) {
        distinctXValues.add(xValuesEntry);
      }
    }
  }

  const xValuesArray = Array.from(distinctXValues);
  const xValueCount = xValuesArray.length;

  // Determine the increment value
  let increment = Math.max(
    Math.floor(xValueCount / (desiredLabelCount - 1)),
    1,
  );

  // Adjust increment if there are fewer x values than desired labels
  increment = Math.min(increment, xValueCount);

  // Determine the tick positions
  const tickPositions = [0]; // Always include the first tick

  for (let i = increment; i < xValueCount - increment; i += increment) {
    tickPositions.push(i);
  }

  // Adjust the last tick position if necessary
  const lastTick = xValueCount - 1;
  if (!tickPositions.includes(lastTick)) {
    tickPositions.push(lastTick);
  }

  // Map tick positions to corresponding x values
  const resultArray: number[] = [];
  tickPositions.forEach((index) => {
    const xValuesArrayEntry = xValuesArray[index];

    if (xValuesArrayEntry !== undefined) {
      resultArray.push(xValuesArrayEntry);
    }
  });

  return resultArray;
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function getClosestDay(date: Dayjs, targetDay: number) {
  // Get the current day of the week (0-6, Sunday is 0)
  const currentDayOfWeek = dayjs(date).day();

  // Calculate the difference between the target day and the current day of the week
  const diff = targetDay - currentDayOfWeek;

  // Calculate the number of days to the previous and next target day
  const daysToPreviousTargetDay = diff > 0 ? diff - 7 : diff;
  const daysToNextTargetDay = diff > 0 ? diff : diff + 7;

  // Get the date of the previous and next target day
  const previousTargetDay = dayjs(date).add(daysToPreviousTargetDay, 'day');
  const nextTargetDay = dayjs(date).add(daysToNextTargetDay, 'day');

  // Return the target day that is closest to the given date
  return dayjs(date).diff(previousTargetDay) < nextTargetDay.diff(dayjs(date))
    ? previousTargetDay
    : nextTargetDay;
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function getClosestWeekRange(startDate: Dayjs, endDate: Dayjs) {
  if (
    !startDate ||
    !dayjs(startDate).isValid() ||
    !endDate ||
    !dayjs(endDate).isValid()
  ) {
    return null;
  }

  let start = startDate;
  let end = endDate;

  // account for incorrectly passed dates sequence
  if (start.isAfter(end)) {
    const temp = start;
    start = end;
    end = temp;
  }

  start = getClosestDay(start, 1); // 1 = monday
  end = getClosestDay(end, 0); // 0 = sunday

  // account for incorrectly calculated dates sequence
  if (start.isAfter(end)) {
    end = end.add(7, 'day');
  }

  return { start, end };
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function getHighchartsDate(
  intl: Services['intl'],
  datesService: Services['dates'],
  timestamp: number,
  aggregation: 'day' | 'week' | 'month',
) {
  const highchartsDate = Highcharts.dateFormat(
    datesService.highchartsLabelFormats[aggregation],
    timestamp,
  );

  if (aggregation === 'week') {
    return intl.t(tKey('general.week_of'), { date: highchartsDate });
  }

  return highchartsDate;
}
